import {
  CLAUDE_EXAM_DOMAINS,
} from "./claudePracticeDomains";

export const CLAUDE_PRACTICE_SETTING_FIELDS = {
  enabled: "claude_exam_enabled",
  questionCount: "claude_exam_question_count",
  timeMinutes: "claude_exam_time_minutes",
  readinessScore: "claude_exam_readiness_score",
  domainMinimum: "claude_exam_domain_minimum",
};

export const DEFAULT_CLAUDE_PRACTICE_SETTINGS = {
  enabled: true,
  questionCount: 60,
  timeMinutes: 120,
  readinessScore: 85,
  domainMinimum: 75,
};

const clampInteger = (value, fallback, minimum, maximum) => {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(maximum, Math.max(minimum, parsed));
};

export const normalizeClaudePracticeSettings = (rows = []) => {
  const values = (Array.isArray(rows) ? rows : []).reduce((result, row) => {
    result[row.fieldname] = row.fieldvalue;
    return result;
  }, {});

  return {
    enabled: String(values[CLAUDE_PRACTICE_SETTING_FIELDS.enabled] ?? "true").toLowerCase() !== "false",
    questionCount: clampInteger(
      values[CLAUDE_PRACTICE_SETTING_FIELDS.questionCount],
      DEFAULT_CLAUDE_PRACTICE_SETTINGS.questionCount,
      20,
      100
    ),
    timeMinutes: clampInteger(
      values[CLAUDE_PRACTICE_SETTING_FIELDS.timeMinutes],
      DEFAULT_CLAUDE_PRACTICE_SETTINGS.timeMinutes,
      30,
      240
    ),
    readinessScore: clampInteger(
      values[CLAUDE_PRACTICE_SETTING_FIELDS.readinessScore],
      DEFAULT_CLAUDE_PRACTICE_SETTINGS.readinessScore,
      50,
      100
    ),
    domainMinimum: clampInteger(
      values[CLAUDE_PRACTICE_SETTING_FIELDS.domainMinimum],
      DEFAULT_CLAUDE_PRACTICE_SETTINGS.domainMinimum,
      0,
      100
    ),
  };
};

export const buildBlueprintAllocation = (domains, questionCount) => {
  const entries = Object.entries(domains);
  const allocations = entries.map(([key, domain]) => {
    const exact = (questionCount * domain.weight) / 100;
    return { key, count: Math.floor(exact), remainder: exact - Math.floor(exact) };
  });
  let remaining = questionCount - allocations.reduce((total, item) => total + item.count, 0);

  allocations
    .sort((left, right) => right.remainder - left.remainder)
    .forEach((item) => {
      if (remaining > 0) {
        item.count += 1;
        remaining -= 1;
      }
    });

  return Object.fromEntries(allocations.map(({ key, count }) => [key, count]));
};

const shuffle = (items) => {
  const result = [...items];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [result[index], result[swapIndex]] = [result[swapIndex], result[index]];
  }
  return result;
};

export const selectClaudePracticeQuestionIds = (
  questions,
  settings = DEFAULT_CLAUDE_PRACTICE_SETTINGS
) => {
  const allocation = buildBlueprintAllocation(
    CLAUDE_EXAM_DOMAINS,
    settings.questionCount
  );
  const selected = [];

  Object.entries(allocation).forEach(([domain, count]) => {
    const conceptGroups = questions
      .filter((item) => item.domain === domain)
      .reduce((groups, item) => {
        const key = item.concept || item.id;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(item);
        return groups;
      }, new Map());

    shuffle([...conceptGroups.values()])
      .slice(0, count)
      .forEach((variants) => {
        const chosen = variants[Math.floor(Math.random() * variants.length)];
        selected.push(chosen.id);
      });
  });

  return shuffle(selected);
};
